import React from 'react';
import {
  Badge,
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, FiberManualRecord, Refresh, Remove, ChatOutlined } from '@mui/icons-material';
import { AgentRunProvider } from '../../context/AgentRunContext';
import { ChatPanel } from '../panels/ChatPanel';
import { useAgentRun } from '../../hooks/useAgentRun';

function GlobalChatLauncherInner({ open, setOpen, onRestartConversation }) {
  const { state } = useAgentRun();
  const [lastReadMessageId, setLastReadMessageId] = React.useState<string | null>(null);

  const latestCompletedMessage = React.useMemo(
    () => [...state.messages].reverse().find((message) => message.role === 'assistant' || message.role === 'hitl' || message.role === 'error') || null,
    [state.messages],
  );

  React.useEffect(() => {
    if (!open || !latestCompletedMessage?.id) {
      return;
    }
    setLastReadMessageId(latestCompletedMessage.id);
  }, [open, latestCompletedMessage?.id]);

  const hasUnreadCompletedMessage = Boolean(
    !open
    && latestCompletedMessage?.id
    && latestCompletedMessage.id !== lastReadMessageId,
  );
  const unreadCount = hasUnreadCompletedMessage ? 1 : 0;

  return (
    <>
      <Tooltip title="Open assistant" placement="left">
        <Badge
          color="warning"
          overlap="circular"
          badgeContent={unreadCount > 0 ? unreadCount : 0}
          invisible={unreadCount === 0}
          sx={{
            position: 'fixed',
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: (theme) => theme.zIndex.tooltip,
            opacity: open ? 0.18 : 1,
            transform: open ? 'scale(0.94)' : 'scale(1)',
            transition: 'opacity 180ms ease, transform 180ms ease',
            '& .MuiBadge-badge': {
              fontWeight: 700,
              minWidth: 22,
              height: 22,
              bgcolor: '#B91C1C',
              color: '#fff',
              boxShadow: '0 0 0 2px #fff',
            },
          }}
        >
          <Paper
            component="button"
            type="button"
            elevation={0}
            onClick={() => setOpen(true)}
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              border: '1px solid rgba(127, 29, 29, 0.28)',
              bgcolor: '#991B1B',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              color: '#ffffff',
              boxShadow: '0 10px 22px rgba(127, 29, 29, 0.28)',
              transition: 'transform 180ms ease, filter 180ms ease, opacity 180ms ease',
              '&:hover': {
                transform: open ? 'scale(0.94)' : 'translateY(-1px) scale(1.01)',
                filter: 'drop-shadow(0 10px 20px rgba(127, 29, 29, 0.28))',
              },
            }}
          >
            <ChatOutlined sx={{ fontSize: 21 }} />
          </Paper>
        </Badge>
      </Tooltip>

      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: (theme) => theme.zIndex.tooltip,
        }}
      >
        {open && (
          <Paper
            elevation={14}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 24 },
              bottom: { xs: 8, sm: 24 },
              width: { xs: 'min(calc(100vw - 16px), 390px)', sm: 360, lg: 390 },
              height: { xs: 'min(68vh, 480px)', sm: 'min(72vh, 460px)', lg: 480 },
              maxHeight: { xs: '68vh', sm: '72vh', lg: 480 },
              borderRadius: '18px',
              overflow: 'hidden',
              pointerEvents: 'auto',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.16)',
              background: '#f0f2f5',
              border: '1px solid rgba(17, 24, 39, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: (theme) => theme.zIndex.tooltip,
            }}
            onMouseDownCapture={(e) => {
              e.stopPropagation();
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1.15, borderBottom: '1px solid rgba(17, 24, 39, 0.08)', bgcolor: '#f7f8fa', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#991B1B', border: '1px solid rgba(127, 29, 29, 0.28)', display: 'grid', placeItems: 'center', color: '#fff' }}>
                  <ChatOutlined sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#111827', letterSpacing: 0.2 }}>
                    Assistant
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FiberManualRecord sx={{ fontSize: 10, color: state.connectionStatus === 'open' ? '#16a34a' : '#f59e0b' }} />
                    <Typography sx={{ fontSize: 12, color: '#475569' }}>
                      {state.connectionStatus === 'open' ? 'Connected' : 'Connecting'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title="Restart conversation" placement="bottom">
                  <IconButton onClick={onRestartConversation} size="small" sx={{ color: '#4b5563' }}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Minimize" placement="bottom">
                  <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: '#475569' }}>
                    <Remove fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" placement="bottom">
                  <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: '#475569' }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ p: 0, height: '100%', minHeight: 0 }}>
              <Box sx={{ height: '100%', minHeight: 0 }}>
                <ChatPanel hideHeader />
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );
}

export default function GlobalChatLauncher({ hidden = false }: { hidden?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [sessionVersion, setSessionVersion] = React.useState(0);

  const handleRestartConversation = React.useCallback(() => {
    setSessionVersion((value) => value + 1);
    setOpen(true);
  }, []);

  return (
    <Box sx={{ display: hidden ? 'none' : 'contents' }}>
      <AgentRunProvider key={`agent-launcher-${sessionVersion}`}>
        <GlobalChatLauncherInner
          open={open}
          setOpen={setOpen}
          onRestartConversation={handleRestartConversation}
        />
      </AgentRunProvider>
    </Box>
  );
}
